import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../print.css';
import { useAuth } from '../contexts/AuthContext';

// Company Logo as Base64
const COMPANY_LOGO = '/images/qat.png';

const PInvoicesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const invoiceRef = useRef(null);
  const formRef = useRef(null);
  
  const [formData, setFormData] = useState({
    invoiceNumber: `INV-${Date.now()}`,
    serviceDate: new Date().toISOString().split('T')[0],
    clientName: '',
    clientAddress: '',
    clientPhone: '',
    clientEmail: '',
    clientModel: '',
    isEstimate: false,
    lineItems: [{
      qty: 1,
      description: '',
      price: 0,
      amount: 0
    }],
    laborPerformed: '',
    subtotal: 0,
    taxRate: 0.0825,
    taxAmount: 0,
    discountMode: 'amount',
    discount: 0,
    discountPercent: 0,
    appliedDiscount: 0,
    totalAmount: 0,
    notes: '',
    terms: 'Terms: Net Cash. No Goods Held Over 30 Days\n\nNotice: This invoice is valid only if issued and received directly from QualityAirofTexas@gmail.com.'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [customTemplates, setCustomTemplates] = useState({});
  const [recentClients, setRecentClients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [newTemplateData, setNewTemplateData] = useState({
    name: '',
    items: [{ qty: 1, description: '', price: 0, amount: 0 }],
    labor: ''
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareStatus, setShareStatus] = useState('');

  // Customer database search (replaces localStorage recent clients)
  const [dbCustomers, setDbCustomers] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerSearchTimeout, setCustomerSearchTimeout] = useState(null);

  // Mobile-first sticky bar visibility
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Show sticky bar after scrolling on mobile
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 250);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-Save to LocalStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.clientName || formData.clientAddress || formData.lineItems[0].description) {
        localStorage.setItem('invoiceDraft', JSON.stringify(formData));
        setHasDraft(true);
      }
    }, 2000); // Save after 2 seconds of inactivity
    
    return () => clearTimeout(timer);
  }, [formData]);

  // Load draft, custom templates, and recent clients on mount
  useEffect(() => {
    const draft = localStorage.getItem('invoiceDraft');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setHasDraft(true);
      } catch (e) {
        console.error('Failed to parse draft:', e);
      }
    }

    // Load custom templates from API
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${window.location.origin}/api/public/service-templates`);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && data.data.templates) {
            // Convert API format to component format
            const templatesObj = {};
            data.data.templates.forEach(template => {
              if (!template.isDefault) {
                templatesObj[`db-${template.id}`] = {
                  id: template.id,
                  name: template.name,
                  items: template.items,
                  labor: template.labor
                };
              }
            });
            setCustomTemplates(templatesObj);
          }
        }
      } catch (e) {
        console.error('Failed to fetch templates from API:', e);
      }
    };
    fetchTemplates();

    // Load recent clients
    const clients = localStorage.getItem('recentClients');
    if (clients) {
      try {
        setRecentClients(JSON.parse(clients));
      } catch (e) {
        console.error('Failed to parse clients:', e);
      }
    }

    // Load suggestions
    const savedSuggestions = localStorage.getItem('serviceSuggestions');
    if (savedSuggestions) {
      try {
        setSuggestions(JSON.parse(savedSuggestions));
      } catch (e) {
        console.error('Failed to parse suggestions:', e);
      }
    }
  }, []);

  // Calculate totals
  useEffect(() => {
    let currentSubtotal = 0;
    formData.lineItems.forEach(item => {
      currentSubtotal += parseFloat(item.amount || 0);
    });
    
    const currentTaxAmount = currentSubtotal * parseFloat(formData.taxRate || 0);
    const pct = parseFloat(formData.discountPercent || 0);
    const amt = parseFloat(formData.discount || 0);
    let discountRaw =
      formData.discountMode === 'percent'
        ? currentSubtotal * (pct / 100)
        : amt;
    const maxDiscount = Math.max(0, currentSubtotal + currentTaxAmount);
    discountRaw = Math.min(Math.max(0, discountRaw), maxDiscount);
    const appliedDiscount = discountRaw;
    const currentTotalAmount = currentSubtotal + currentTaxAmount - appliedDiscount;

    setFormData(prev => ({
      ...prev,
      subtotal: currentSubtotal,
      taxAmount: currentTaxAmount,
      appliedDiscount,
      totalAmount: currentTotalAmount
    }));
  }, [formData.lineItems, formData.taxRate, formData.discount, formData.discountPercent, formData.discountMode]);

  const discountLineLabel = useMemo(() => {
    const applied = Number(formData.appliedDiscount || 0);
    if (applied <= 0) return '';
    if (formData.discountMode === 'percent' && Number(formData.discountPercent) > 0) {
      return `Discount (${Number(formData.discountPercent).toFixed(2)}%)`;
    }
    return 'Discount ($)';
  }, [formData.appliedDiscount, formData.discountMode, formData.discountPercent]);

  // Service Templates
  const serviceTemplates = {
    'ac-repair': {
      name: 'AC Unit Repair',
      items: [
        { qty: 1, description: 'AC Unit Diagnosis', price: 85, amount: 85 },
        { qty: 1, description: 'Repair/Replace Faulty Component', price: 250, amount: 250 }
      ],
      labor: 'Diagnosed AC unit, identified faulty compressor, replaced with new unit, tested system operation, recharged refrigerant to proper levels.'
    },
    'maintenance': {
      name: 'Annual Maintenance',
      items: [
        { qty: 1, description: 'Full System Inspection', price: 120, amount: 120 },
        { qty: 1, description: 'Filter Replacement', price: 45, amount: 45 },
        { qty: 1, description: 'Coil Cleaning', price: 85, amount: 85 }
      ],
      labor: 'Performed complete system inspection, replaced air filters, cleaned condenser coils, checked refrigerant levels, tested thermostat operation.'
    },
    'installation': {
      name: 'New Installation',
      items: [
        { qty: 1, description: 'New AC Unit - 3 Ton', price: 2500, amount: 2500 },
        { qty: 1, description: 'Installation Labor', price: 800, amount: 800 },
        { qty: 1, description: 'Materials & Supplies', price: 300, amount: 300 }
      ],
      labor: 'Removed old unit, installed new 3-ton AC system, connected electrical and refrigerant lines, tested for proper operation and efficiency.'
    },
    'filter-change': {
      name: 'Filter Replacement',
      items: [
        { qty: 1, description: 'Premium Air Filter', price: 35, amount: 35 },
        { qty: 1, description: 'Service Call', price: 50, amount: 50 }
      ],
      labor: 'Replaced air filter with high-efficiency filter, inspected system operation.'
    }
  };

  // Load Service Template
  const loadServiceTemplate = (templateKey) => {
    if (!templateKey) return;
    
    // Check both default and custom templates
    const allTemplates = { ...serviceTemplates, ...customTemplates };
    const template = allTemplates[templateKey];
    
    if (template) {
      setFormData(prev => ({
        ...prev,
        lineItems: template.items,
        laborPerformed: template.labor
      }));
    }
  };

  // Save Custom Template
  const saveCustomTemplate = async (templateData) => {
    try {
      const response = await fetch(`${window.location.origin}/api/public/service-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: templateData.name,
          items: templateData.items,
          labor: templateData.labor,
          createdBy: 'public'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data.template) {
          const template = data.data.template;
          const key = `db-${template.id}`;
          setCustomTemplates(prev => ({
            ...prev,
            [key]: {
              id: template.id,
              name: template.name,
              items: template.items,
              labor: template.labor
            }
          }));
          alert('Template saved successfully!');
        }
      } else {
        throw new Error('Failed to save template');
      }
    } catch (e) {
      console.error('Error saving template:', e);
      alert('Failed to save template. Please try again.');
    }
  };

  // Delete Custom Template
  const deleteCustomTemplate = async (key) => {
    try {
      // Extract ID from key (format: "db-{id}")
      const templateId = key.replace('db-', '');
      
      const response = await fetch(`${window.location.origin}/api/public/service-templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const newTemplates = { ...customTemplates };
        delete newTemplates[key];
        setCustomTemplates(newTemplates);
      } else {
        throw new Error('Failed to delete template');
      }
    } catch (e) {
      console.error('Error deleting template:', e);
      alert('Failed to delete template. Please try again.');
    }
  };

  // ============================================
  // CUSTOMER DATABASE SEARCH (Auto-fill)
  // ============================================
  const searchCustomers = async (query) => {
    if (!query || query.trim().length < 2) {
      setDbCustomers([]);
      return;
    }
    try {
      const res = await fetch(`/api/public/customers/search?q=${encodeURIComponent(query)}&limit=8`);
      if (res.ok) {
        const data = await res.json();
        setDbCustomers(data.data?.customers || []);
      }
    } catch (e) {
      console.error('Customer search failed:', e);
    }
  };

  const handleClientNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, clientName: value }));
    setShowCustomerSuggestions(true);

    // Debounced search
    if (customerSearchTimeout) clearTimeout(customerSearchTimeout);
    const timer = setTimeout(() => searchCustomers(value), 250);
    setCustomerSearchTimeout(timer);
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      clientName: customer.name || '',
      clientEmail: customer.email || '',
      clientPhone: customer.phone || '',
      clientAddress: customer.address || '',
      clientModel: customer.acModel || ''
    }));
    setShowCustomerSuggestions(false);
    setDbCustomers([]);

    // Haptic feedback on supported devices
    if (navigator.vibrate) navigator.vibrate(10);
  };

  // Save to Recent Clients
  const saveToRecentClients = (clientData) => {
    const newClient = {
      id: Date.now(),
      name: clientData.clientName,
      address: clientData.clientAddress,
      phone: clientData.clientPhone,
      email: clientData.clientEmail,
      model: clientData.clientModel,
      lastUsed: new Date().toISOString()
    };

    // Keep only last 10 clients
    const updatedClients = [newClient, ...recentClients.filter(c => c.name !== clientData.clientName)].slice(0, 10);
    setRecentClients(updatedClients);
    localStorage.setItem('recentClients', JSON.stringify(updatedClients));
  };

  // Load Recent Client
  const loadRecentClient = (clientId) => {
    const client = recentClients.find(c => c.id === clientId);
    if (client) {
      setFormData(prev => ({
        ...prev,
        clientName: client.name,
        clientAddress: client.address,
        clientPhone: client.phone || '',
        clientEmail: client.email || '',
        clientModel: client.model || ''
      }));
    }
  };

  // Add to Suggestions
  const addToSuggestions = (description, price) => {
    if (!description || !price) return;
    
    const existing = suggestions.find(s => s.description.toLowerCase() === description.toLowerCase());
    if (existing) {
      // Update frequency
      existing.count = (existing.count || 1) + 1;
      existing.avgPrice = ((existing.avgPrice * (existing.count - 1)) + parseFloat(price)) / existing.count;
    } else {
      suggestions.push({
        description,
        avgPrice: parseFloat(price),
        count: 1
      });
    }
    
    const sorted = suggestions.sort((a, b) => b.count - a.count).slice(0, 20);
    setSuggestions(sorted);
    localStorage.setItem('serviceSuggestions', JSON.stringify(sorted));
  };

  // Get Suggestions
  const getSuggestions = (input) => {
    if (!input || input.length < 2) return [];
    return suggestions
      .filter(s => s.description.toLowerCase().includes(input.toLowerCase()))
      .slice(0, 5);
  };

  // Load Draft
  const loadDraft = () => {
    const draft = localStorage.getItem('invoiceDraft');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setFormData(parsed);
        setHasDraft(false);
        alert('Draft loaded successfully!');
      } catch (e) {
        alert('Failed to load draft');
      }
    }
  };

  // Clear Draft
  const clearDraft = () => {
    localStorage.removeItem('invoiceDraft');
    setHasDraft(false);
    alert('Draft cleared!');
  };

  // Copy Last Invoice
  const copyLastInvoice = () => {
    const lastInvoice = localStorage.getItem('lastInvoice');
    if (lastInvoice) {
      try {
        const parsed = JSON.parse(lastInvoice);
        setFormData({
          ...parsed,
          invoiceNumber: `INV-${Date.now()}`,
          serviceDate: new Date().toISOString().split('T')[0],
          isEstimate: false
        });
        alert('Last invoice copied!');
      } catch (e) {
        alert('Failed to copy last invoice');
      }
    } else {
      alert('No previous invoice found');
    }
  };

  // Convert Estimate to Invoice
  const convertEstimateToInvoice = async (estimateNumber) => {
    if (!estimateNumber) {
      alert('Please enter estimate number');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await fetch(`/api/public/estimates/${estimateNumber}`);
      
      if (!response.ok) {
        throw new Error('Estimate not found');
      }
      
      const data = await response.json();
      const estimate = data.data;
      
      // Parse notes to get client info
      let clientInfo = {};
      try {
        clientInfo = JSON.parse(estimate.notes || '{}');
      } catch (e) {
        clientInfo = estimate.customFields || {};
      }
      
      setFormData({
        invoiceNumber: `INV-${Date.now()}`,
        serviceDate: new Date().toISOString().split('T')[0],
        clientName: clientInfo.clientName || '',
        clientAddress: clientInfo.clientAddress || '',
        clientPhone: clientInfo.clientPhone || '',
        clientEmail: clientInfo.clientEmail || '',
        clientModel: clientInfo.clientModel || '',
        isEstimate: false,
        lineItems: estimate.lineItems || [{ qty: 1, description: '', price: 0, amount: 0 }],
        laborPerformed: clientInfo.laborPerformed || '',
        subtotal: estimate.subTotal || 0,
        taxRate: estimate.taxRate || 0.0825,
        taxAmount: estimate.taxAmount || 0,
        discountMode: clientInfo.discountMode === 'percent' ? 'percent' : 'amount',
        discount: clientInfo.discount != null ? clientInfo.discount : 0,
        discountPercent: clientInfo.discountPercent != null ? clientInfo.discountPercent : 0,
        appliedDiscount: 0,
        totalAmount: estimate.totalAmount || 0,
        notes: '',
        terms:
          'Terms: Net Cash. No Goods Held Over 30 Days\n\nNotice: This invoice is valid only if issued and received directly from QualityAirofTexas@gmail.com.'
      });
      
      alert(`Estimate ${estimateNumber} converted to invoice!`);
    } catch (err) {
      alert(err.message || 'Failed to load estimate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLineItemChange = (index, e) => {
    const { name, value } = e.target;
    const updatedLineItems = [...formData.lineItems];
    const item = updatedLineItems[index];
    item[name] = value;
    
    if (name === 'qty' || name === 'price') {
      const qty = parseFloat(item.qty || 0);
      const price = parseFloat(item.price || 0);
      item.amount = qty * price;
    }
    
    updatedLineItems[index] = item;
    setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { qty: 1, description: '', price: 0, amount: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length > 1) {
      const updatedLineItems = [...formData.lineItems];
      updatedLineItems.splice(index, 1);
      setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess(false);

    if (!formData.clientName || !formData.clientAddress) {
      setError('Please fill in client name and address.');
      setIsLoading(false);
      return;
    }

    try {
      const { appliedDiscount, ...rest } = formData;
      const payload = {
        ...rest,
        discount: Number(appliedDiscount || 0)
      };

      const endpoint = formData.isEstimate ? '/api/public/estimates' : '/api/public/invoices';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to save ${formData.isEstimate ? 'estimate' : 'invoice'}`);
      }

      setSuccess(true);
      
      // Save as last invoice for copy feature
      if (!formData.isEstimate) {
        localStorage.setItem('lastInvoice', JSON.stringify(formData));
      }
      
      // Save to recent clients
      saveToRecentClients(formData);
      
      // Save line items to suggestions
      formData.lineItems.forEach(item => {
        if (item.description && item.price) {
          addToSuggestions(item.description, item.price);
        }
      });
      
      // Clear draft after successful save
      localStorage.removeItem('invoiceDraft');
      setHasDraft(false);
      
      alert(`${formData.isEstimate ? 'Estimate' : 'Invoice'} saved successfully!`);
      
      // Generate and send PDF via email
      try {
        const pdf = await generatePDF();
        await sendPDFEmail(pdf);
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't show error, invoice was saved successfully
      }

      // Next document number (avoids UNIQUE duplicate on immediate second save — same INV-/EST-)
      setFormData((prev) => ({
        ...prev,
        invoiceNumber: prev.isEstimate ? `EST-${Date.now()}` : `INV-${Date.now()}`
      }));

      const goAdminAfterEstimate =
        formData.isEstimate && user && (user.role === 'admin' || user.role === 'manager');
      if (goAdminAfterEstimate) {
        navigate('/admin/estimates');
      }
      
    } catch (err) {
      console.error('Error saving invoice:', err);
      setError(err.message || 'Failed to save invoice. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPos = 20;

      // Colors
      const primaryColor = [128, 0, 128]; // Purple
      const secondaryColor = [255, 140, 0]; // Orange
      const darkGray = [64, 64, 64];
      const lightGray = [128, 128, 128];

      // Try to add company logo
      try {
        // Load logo from public folder
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = window.location.origin + COMPANY_LOGO;
        
        // Wait for image to load
        await new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              pdf.addImage(img, 'PNG', 15, yPos - 5, 40, 16);
              resolve();
            } catch (err) {
              console.log('Image add failed:', err);
              reject(err);
            }
          };
          img.onerror = () => {
            console.log('Image load failed');
            reject();
          };
          // Timeout after 2 seconds
          setTimeout(() => reject(), 2000);
        }).catch(() => {
          // Fallback: draw logo placeholder
          pdf.setDrawColor(...primaryColor);
          pdf.setLineWidth(0.5);
          pdf.rect(15, yPos - 5, 35, 14);
          pdf.setFontSize(8);
          pdf.setTextColor(...primaryColor);
          pdf.text('QAT', 20, yPos + 3);
          pdf.text('LOGO', 20, yPos + 7);
        });
      } catch (logoError) {
        console.log('Logo error:', logoError);
        // Fallback: draw logo placeholder
        pdf.setDrawColor(...primaryColor);
        pdf.setLineWidth(0.5);
        pdf.rect(15, yPos - 5, 35, 14);
        pdf.setFontSize(8);
        pdf.setTextColor(...primaryColor);
        pdf.text('QAT', 20, yPos + 3);
        pdf.text('LOGO', 20, yPos + 7);
      }

      // Company name and info (right side)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      pdf.setTextColor(...primaryColor);
      pdf.text('Quality Air of Texas LLC', pageWidth - 15, yPos + 5, { align: 'right' });
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkGray);
      pdf.text('6023 Pennworth Ln, Houston, TX 77084', pageWidth - 15, yPos + 11, { align: 'right' });
      pdf.text('Phone: (713) 922-5702', pageWidth - 15, yPos + 15, { align: 'right' });
      pdf.text('https://qualityairoftexas.com', pageWidth - 15, yPos + 19, { align: 'right' });
      pdf.text('Email: qualityairoftexas@gmail.com', pageWidth - 15, yPos + 23, { align: 'right' });

      yPos += 33;

      // Invoice/Estimate title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(24);
      pdf.setTextColor(...secondaryColor);
      pdf.text(formData.isEstimate ? 'ESTIMATE' : 'INVOICE', pageWidth - 15, yPos, { align: 'right' });
      
      if (formData.isEstimate) {
        pdf.setFontSize(8);
        pdf.setTextColor(255, 0, 0);
        pdf.text('✓ Valid for 30 DAYS', pageWidth - 15, yPos + 5, { align: 'right' });
      }

      yPos += 15;

      // Invoice details and client info side by side
      pdf.setDrawColor(...primaryColor);
      pdf.setLineWidth(0.5);
      pdf.line(15, yPos, pageWidth - 15, yPos);
      yPos += 8;

      // Left side - Client Info
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...primaryColor);
      pdf.text('BILL TO:', 15, yPos);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(...darkGray);
      yPos += 6;
      pdf.text(formData.clientName || '_________________', 15, yPos);
      yPos += 5;
      pdf.text(formData.clientAddress || '_________________', 15, yPos);
      yPos += 5;
      pdf.text('Phone: ' + (formData.clientPhone || '_________________'), 15, yPos);
      if (formData.clientEmail) {
        yPos += 5;
        pdf.text('Email: ' + formData.clientEmail, 15, yPos);
      }
      if (formData.clientModel) {
        yPos += 5;
        pdf.text('Model: ' + formData.clientModel, 15, yPos);
      }

      // Right side - Invoice Info
      const rightX = pageWidth / 2 + 10;
      yPos = yPos - (formData.clientModel ? 20 : 15);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...lightGray);
      pdf.text('Invoice Number:', rightX, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...darkGray);
      pdf.text(formData.invoiceNumber, pageWidth - 15, yPos, { align: 'right' });
      
      yPos += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...lightGray);
      pdf.text('Date:', rightX, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...darkGray);
      pdf.text(new Date(formData.serviceDate).toLocaleDateString('en-US'), pageWidth - 15, yPos, { align: 'right' });
      
      yPos = Math.max(yPos + 15, yPos + (formData.clientModel ? 0 : 5));

      // Line items table
      const tableColumn = ['QTY', 'DESCRIPTION', 'UNIT PRICE', 'AMOUNT'];
      const tableRows = formData.lineItems.map(item => [
        item.qty.toString(),
        item.description || '',
        '$' + parseFloat(item.price || 0).toFixed(2),
        '$' + parseFloat(item.amount || 0).toFixed(2)
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        headStyles: {
          fillColor: primaryColor,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'left'
        },
        columnStyles: {
          0: { cellWidth: 20, halign: 'center' },
          1: { cellWidth: 90, halign: 'left' },
          2: { cellWidth: 35, halign: 'right' },
          3: { cellWidth: 35, halign: 'right' }
        },
        styles: {
          fontSize: 9,
          cellPadding: 4,
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250]
        },
        margin: { left: 15, right: 15 }
      });

      yPos = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + 10 : yPos + 50;

      // Labor performed section
      if (formData.laborPerformed) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...primaryColor);
        pdf.text('LABOR PERFORMED:', 15, yPos);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(...darkGray);
        const laborLines = pdf.splitTextToSize(formData.laborPerformed, pageWidth - 30);
        pdf.text(laborLines, 15, yPos + 5);
        yPos += 5 + (laborLines.length * 5) + 5;
      } else {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(9);
        pdf.setTextColor(...lightGray);
        pdf.text('We have one year workmanship warranty on labor and manufacture warranty on parts.', 15, yPos);
        yPos += 10;
      }

      // Totals section
      const totalsX = pageWidth - 70;
      const totalsValueX = pageWidth - 15;

      pdf.setDrawColor(...lightGray);
      pdf.setLineWidth(0.3);
      pdf.line(totalsX - 5, yPos, pageWidth - 15, yPos);
      yPos += 6;

      // Subtotal
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...darkGray);
      pdf.text('Subtotal:', totalsX, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text('$' + formData.subtotal.toFixed(2), totalsValueX, yPos, { align: 'right' });
      yPos += 6;

      // Tax
      pdf.setFont('helvetica', 'bold');
      pdf.text('Tax (' + (formData.taxRate * 100).toFixed(2) + '%): ', totalsX, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text('$' + formData.taxAmount.toFixed(2), totalsValueX, yPos, { align: 'right' });
      yPos += 6;

      // Discount
      const appliedDisc = Number(formData.appliedDiscount || 0);
      if (appliedDisc > 0) {
        const pct = Number(formData.discountPercent || 0);
        const discountTitle =
          formData.discountMode === 'percent' && pct > 0
            ? `Discount (${pct.toFixed(2)}%)`
            : 'Discount ($)';
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 0, 0);
        pdf.text(discountTitle + ':', totalsX, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text('-$' + appliedDisc.toFixed(2), totalsValueX, yPos, { align: 'right' });
        yPos += 6;
      }

      // Total line
      pdf.setDrawColor(...primaryColor);
      pdf.setLineWidth(0.5);
      pdf.line(totalsX - 5, yPos, pageWidth - 15, yPos);
      yPos += 7;

      // Total amount
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(...primaryColor);
      pdf.text('TOTAL:', totalsX, yPos);
      pdf.setFontSize(16);
      pdf.text('$' + formData.totalAmount.toFixed(2), totalsValueX, yPos, { align: 'right' });
      yPos += 10;

      // Footer section
      if (yPos > pageHeight - 50) {
        pdf.addPage();
        yPos = 20;
      }

      // Warning text
      pdf.setDrawColor(...lightGray);
      pdf.setLineWidth(0.3);
      pdf.line(15, yPos, pageWidth - 15, yPos);
      yPos += 6;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(...darkGray);
      pdf.text('IMPORTANT NOTICE:', 15, yPos);
      yPos += 5;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(...lightGray);
      const warningText = 'We Will Not Be Responsible For Loss or Damage Caused By: Fire, Theft, Testing, Or Any Other Causes beyond Our Control.';
      const warningLines = pdf.splitTextToSize(warningText, pageWidth - 30);
      pdf.text(warningLines, 15, yPos);
      yPos += warningLines.length * 4 + 3;

      // Terms
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(...darkGray);
      pdf.text('TERMS & CONDITIONS:', 15, yPos);
      yPos += 5;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(...lightGray);
      const termsLines = pdf.splitTextToSize(formData.terms, pageWidth - 30);
      pdf.text(termsLines, 15, yPos);
      yPos += termsLines.length * 4 + 8;

      // Authorized By
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkGray);
      pdf.text('Authorized By: Pejman Ansari', pageWidth - 15, yPos, { align: 'right' });

      // Page border
      pdf.setDrawColor(...primaryColor);
      pdf.setLineWidth(1);
      pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);

      return pdf;
    } catch (error) {
      console.error('PDF Generation Error:', error);
      throw new Error('Failed to generate PDF: ' + error.message);
    }
  };

  const downloadPDF = async () => {
    try {
      const pdf = await generatePDF();
      pdf.save(`${formData.invoiceNumber}.pdf`);
      
      // Send email with PDF
      await sendPDFEmail(pdf);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // ============================================
  // SHARE FEATURES (Multi-channel sharing)
  // ============================================

  // Main Share Handler - Opens modal with all options
  const handleShare = async () => {
    setShowShareModal(true);
    setShareStatus('');
  };

  // Quick native share via Web Share API (mobile)
  const shareNative = async () => {
    try {
      setShareLoading(true);
      setShareStatus('Generating PDF...');

      const pdf = await generatePDF();
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File(
        [pdfBlob],
        `${formData.invoiceNumber}.pdf`,
        { type: 'application/pdf' }
      );

      const shareData = {
        title: `${formData.isEstimate ? 'Estimate' : 'Invoice'} ${formData.invoiceNumber}`,
        text: `${formData.isEstimate ? 'Estimate' : 'Invoice'} from Quality Air of Texas LLC\nTotal: $${formData.totalAmount.toFixed(2)}`,
        files: [pdfFile]
      };

      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        setShareStatus('✅ Shared successfully!');
      } else if (navigator.share) {
        await navigator.share({
          title: shareData.title,
          text: shareData.text
        });
        setShareStatus('✅ Shared (text only - device cannot share files)');
      } else {
        setShareStatus('⚠️ Native share not available on this device. Please use other options below.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share failed:', err);
        setShareStatus('❌ Share failed: ' + err.message);
      } else {
        setShareStatus('');
      }
    } finally {
      setShareLoading(false);
    }
  };

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const phone = formData.clientPhone.replace(/\D/g, '');
    const docType = formData.isEstimate ? 'estimate' : 'invoice';
    const message =
      `Hello ${formData.clientName || 'Customer'},\n\n` +
      `Your ${docType} #${formData.invoiceNumber} from *Quality Air of Texas LLC* is ready.\n\n` +
      `📅 Date: ${formData.serviceDate}\n` +
      `💰 Total: *$${formData.totalAmount.toFixed(2)}*\n\n` +
      `📎 The full PDF will be sent to your email shortly.\n\n` +
      `Phone: (713) 922-5702\n` +
      `Email: qualityairoftexas@gmail.com`;

    const whatsappUrl = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, '_blank');
    setShareStatus('✅ WhatsApp opened');
  };

  // Share via SMS
  const shareViaSMS = () => {
    const phone = formData.clientPhone || '';
    const docType = formData.isEstimate ? 'estimate' : 'invoice';
    const message =
      `Quality Air of Texas: Your ${docType} #${formData.invoiceNumber} ` +
      `for $${formData.totalAmount.toFixed(2)} is ready. ` +
      `Check your email for the PDF. Call (713) 922-5702`;

    window.location.href = `sms:${phone}?body=${encodeURIComponent(message)}`;
    setShareStatus('✅ SMS app opened');
  };

  // Share via Email (mailto)
  const shareViaEmail = () => {
    const docType = formData.isEstimate ? 'Estimate' : 'Invoice';
    const subject = `${docType} ${formData.invoiceNumber} - Quality Air of Texas`;
    const body =
      `Hello ${formData.clientName || 'Customer'},\n\n` +
      `Please find your ${docType.toLowerCase()} details below:\n\n` +
      `${docType} Number: ${formData.invoiceNumber}\n` +
      `Date: ${formData.serviceDate}\n` +
      `Total: $${formData.totalAmount.toFixed(2)}\n\n` +
      `Thank you for your business!\n\n` +
      `Quality Air of Texas LLC\n` +
      `6023 Pennworth Ln, Houston, TX 77084\n` +
      `Phone: (713) 922-5702\n` +
      `Email: qualityairoftexas@gmail.com\n` +
      `Web: https://qualityairoftexas.com`;

    const recipient = formData.clientEmail || '';
    window.location.href = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShareStatus('✅ Email client opened');
  };

  // Send PDF directly to client's email
  const sendToClient = async () => {
    if (!formData.clientEmail) {
      setShareStatus('❌ Please enter client email first in the form above');
      return;
    }

    try {
      setShareLoading(true);
      setShareStatus('📧 Generating PDF and sending to client...');

      const pdf = await generatePDF();
      const pdfBase64 = pdf.output('dataurlstring').split(',')[1];

      const response = await fetch('/api/public/invoices/send-to-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceData: {
            ...formData,
            discount: Number(formData.appliedDiscount || 0),
            discountDisplayLabel: discountLineLabel || 'Discount'
          },
          pdfBase64: pdfBase64,
          invoiceNumber: formData.invoiceNumber,
          clientEmail: formData.clientEmail,
          clientName: formData.clientName
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      const data = await response.json();
      if (data.status === 'success') {
        setShareStatus(`✅ Sent to ${formData.clientEmail} successfully!`);
      } else {
        throw new Error(data.message || 'Failed to send');
      }
    } catch (err) {
      console.error('Send to client failed:', err);
      setShareStatus('❌ Failed to send: ' + err.message);
    } finally {
      setShareLoading(false);
    }
  };

  // Copy invoice summary to clipboard
  const copyInvoiceSummary = async () => {
    const docType = formData.isEstimate ? 'ESTIMATE' : 'INVOICE';
    const summary =
      `🔷 ${docType} #${formData.invoiceNumber}\n` +
      `Quality Air of Texas LLC\n` +
      `${'─'.repeat(35)}\n` +
      `📅 Date: ${formData.serviceDate}\n` +
      `👤 Client: ${formData.clientName}\n` +
      `📞 Phone: ${formData.clientPhone || 'N/A'}\n` +
      `📍 Address: ${formData.clientAddress || 'N/A'}\n` +
      `${'─'.repeat(35)}\n` +
      `Items:\n` +
      formData.lineItems.map(item =>
        `  • ${item.qty}x ${item.description} - $${parseFloat(item.amount || 0).toFixed(2)}`
      ).join('\n') +
      `\n${'─'.repeat(35)}\n` +
      `Subtotal: $${formData.subtotal.toFixed(2)}\n` +
      `Tax: $${formData.taxAmount.toFixed(2)}\n` +
      (Number(formData.appliedDiscount || 0) > 0
        ? `${discountLineLabel}: -$${Number(formData.appliedDiscount || 0).toFixed(2)}\n`
        : '') +
      `💰 TOTAL: $${formData.totalAmount.toFixed(2)}\n` +
      `${'─'.repeat(35)}\n` +
      `📞 (713) 922-5702\n` +
      `📧 qualityairoftexas@gmail.com\n` +
      `🌐 https://qualityairoftexas.com`;

    try {
      await navigator.clipboard.writeText(summary);
      setShareStatus('✅ Summary copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = summary;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShareStatus('✅ Summary copied to clipboard!');
    }
  };

  // Print directly
  const printInvoice = async () => {
    try {
      setShareLoading(true);
      setShareStatus('Preparing print...');
      const pdf = await generatePDF();
      pdf.autoPrint();
      const blobUrl = pdf.output('bloburl');
      window.open(blobUrl, '_blank');
      setShareStatus('✅ Print dialog opened');
    } catch (err) {
      console.error('Print failed:', err);
      setShareStatus('❌ Print failed: ' + err.message);
    } finally {
      setShareLoading(false);
    }
  };

  const sendPDFEmail = async (pdf) => {
    try {
      // Convert PDF to base64
      const pdfBase64 = pdf.output('dataurlstring').split(',')[1];
      
      // Send to backend
      await fetch('/api/public/invoices/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoiceData: {
            ...formData,
            discount: Number(formData.appliedDiscount || 0),
            discountDisplayLabel: discountLineLabel || 'Discount'
          },
          pdfBase64: pdfBase64,
          invoiceNumber: formData.invoiceNumber
        }),
      });
      
      console.log('PDF email sent successfully');
    } catch (error) {
      console.error('Failed to send email:', error);
      // Don't show error to user, just log it
    }
  };

  const inputClass = "mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm";

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
            Create Invoice
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            Quality Air of Texas LLC - Invoice Generator
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong>Error: </strong>{error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <strong>Success! </strong>Invoice saved successfully.
          </div>
        )}

        {/* Quick Actions Section */}
        <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-lg shadow-md p-4 md:p-6 mb-6 border-2 border-purple-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Quick Actions
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Draft Notification */}
            {hasDraft && (
              <div className="col-span-full bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">💾 You have a saved draft</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={loadDraft}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg font-medium transition"
                  >
                    Load Draft
                  </button>
                  <button
                    type="button"
                    onClick={clearDraft}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg font-medium transition"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Service Templates */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📋 Service Templates
              </label>
              <div className="flex gap-2">
                <select
                  onChange={(e) => loadServiceTemplate(e.target.value)}
                  className="flex-1 px-3 py-2.5 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                >
                  <option value="">Select a template...</option>
                  <optgroup label="Default Templates">
                    <option value="ac-repair">🔧 AC Unit Repair</option>
                    <option value="maintenance">⚙️ Annual Maintenance</option>
                    <option value="installation">🏗️ New Installation</option>
                    <option value="filter-change">🔄 Filter Replacement</option>
                  </optgroup>
                  {Object.keys(customTemplates).length > 0 && (
                    <optgroup label="Custom Templates">
                      {Object.entries(customTemplates).map(([key, template]) => (
                        <option key={key} value={key}>⭐ {template.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(true)}
                  className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium shadow-md transition whitespace-nowrap"
                  title="Add New Template"
                >
                  ➕
                </button>
              </div>
            </div>

            {/* Recent Clients */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                👥 Recent Clients
              </label>
              {recentClients.length > 0 ? (
                <select
                  onChange={(e) => e.target.value && loadRecentClient(parseInt(e.target.value))}
                  className="w-full px-3 py-2.5 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                >
                  <option value="">Select a recent client...</option>
                  {recentClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} - {client.address.substring(0, 30)}...
                    </option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2.5 text-sm text-gray-500 bg-gray-50 rounded-lg border-2 border-gray-200">
                  💡 No recent clients yet. Save an invoice to see clients here.
                </div>
              )}
            </div>

            {/* Copy Last Invoice */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                📄 Quick Copy
              </label>
              <button
                type="button"
                onClick={copyLastInvoice}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg font-medium shadow-md transition"
              >
                📋 Copy Last Invoice
              </button>
            </div>

            {/* Convert Estimate to Invoice */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🔄 Convert Estimate
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="estimateNumber"
                  placeholder="EST-123..."
                  className="flex-1 px-3 py-2.5 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.getElementById('estimateNumber');
                    convertEstimateToInvoice(input.value);
                  }}
                  className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-lg font-medium shadow-md transition whitespace-nowrap"
                >
                  Convert
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white shadow-lg rounded-lg p-4 md:p-8 mb-6">
          <form ref={formRef} onSubmit={handleSubmit}>
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Invoice Number
                </label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Date
                </label>
                <input
                  type="date"
                  name="serviceDate"
                  value={formData.serviceDate}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="isEstimate"
                  checked={formData.isEstimate}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  This is an Estimate (Valid for 30 DAYS)
                </span>
              </label>
            </div>

            {/* Client Info */}
            <div className="border-t pt-6 mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Client Information
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {/* Client Name with DB Auto-complete */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Client Name <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      💡 Search existing customers
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="clientName"
                      value={formData.clientName}
                      onChange={handleClientNameChange}
                      onFocus={() => formData.clientName && setShowCustomerSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                      placeholder="Type name, email, or phone to search..."
                      className="w-full pl-10 pr-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                      required
                      autoComplete="off"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      🔍
                    </span>
                  </div>

                  {/* Customer Suggestions Dropdown */}
                  {showCustomerSuggestions && dbCustomers.length > 0 && (
                    <div className="absolute z-30 w-full mt-1 bg-white border-2 border-purple-200 rounded-xl shadow-2xl max-h-72 overflow-y-auto">
                      <div className="px-3 py-2 bg-purple-50 border-b border-purple-100 text-xs font-semibold text-purple-700 sticky top-0">
                        ✨ Existing Customers ({dbCustomers.length})
                      </div>
                      {dbCustomers.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); selectCustomer(customer); }}
                          className="w-full text-left px-4 py-3 hover:bg-purple-50 active:bg-purple-100 border-b border-gray-100 last:border-b-0 transition"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                              {customer.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-gray-900 truncate">
                                {customer.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                                {customer.phone && <div>📞 {customer.phone}</div>}
                                {customer.email && <div className="truncate">✉️ {customer.email}</div>}
                                {customer.address && <div className="truncate">📍 {customer.address}</div>}
                              </div>
                              <div className="flex gap-2 mt-1 text-xs">
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                                  {customer.totalInvoices || 0} invoices
                                </span>
                                {parseFloat(customer.totalSpent) > 0 && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                                    ${parseFloat(customer.totalSpent).toFixed(0)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Address - Full Width */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="clientAddress"
                    value={formData.clientAddress}
                    onChange={handleChange}
                    placeholder="Street address, City, State, ZIP"
                    className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    required
                  />
                </div>

                {/* Phone and Email - Side by Side on Desktop */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="clientPhone"
                      value={formData.clientPhone}
                      onChange={handleChange}
                      placeholder="(123) 456-7890"
                      className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="clientEmail"
                      value={formData.clientEmail}
                      onChange={handleChange}
                      placeholder="client@example.com"
                      className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    />
                  </div>
                </div>

                {/* Model - Full Width */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Equipment Model (Optional)
                  </label>
                  <input
                    type="text"
                    name="clientModel"
                    value={formData.clientModel}
                    onChange={handleChange}
                    placeholder="e.g., Carrier 24ACC636A003"
                    className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                  />
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="border-t pt-6 mb-6">
              <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Items / Services
              </h3>
              <div className="space-y-4">
                {formData.lineItems.map((item, index) => (
                  <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-orange-50 rounded-lg border-2 border-purple-200 shadow-sm">
                    {/* Mobile: Stack vertically, Desktop: Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      {/* Description - Full width on mobile */}
                      <div className="md:col-span-6 relative">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description 
                          {suggestions.length > 0 && <span className="text-xs text-gray-500 ml-2">(Start typing for suggestions)</span>}
                        </label>
                        <textarea
                          name="description"
                          value={item.description}
                          onChange={(e) => {
                            handleLineItemChange(index, e);
                            // Auto-fill price from suggestions
                            const matches = getSuggestions(e.target.value);
                            if (matches.length === 1 && e.target.value.toLowerCase() === matches[0].description.toLowerCase()) {
                              const updatedLineItems = [...formData.lineItems];
                              updatedLineItems[index].price = matches[0].avgPrice;
                              updatedLineItems[index].amount = updatedLineItems[index].qty * matches[0].avgPrice;
                              setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
                            }
                          }}
                          placeholder="e.g., AC Unit Repair - Include service details"
                          rows="2"
                          list={`suggestions-${index}`}
                          className="w-full px-3 py-2.5 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 resize-none md:resize-y"
                        />
                        {item.description.length > 2 && getSuggestions(item.description).length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border-2 border-purple-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {getSuggestions(item.description).map((sugg, idx) => (
                              <div
                                key={idx}
                                onClick={() => {
                                  const updatedLineItems = [...formData.lineItems];
                                  updatedLineItems[index].description = sugg.description;
                                  updatedLineItems[index].price = sugg.avgPrice;
                                  updatedLineItems[index].amount = updatedLineItems[index].qty * sugg.avgPrice;
                                  setFormData(prev => ({ ...prev, lineItems: updatedLineItems }));
                                }}
                                className="px-3 py-2 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                              >
                                <div className="font-medium text-gray-800">{sugg.description}</div>
                                <div className="text-sm text-gray-600">
                                  Avg: ${sugg.avgPrice.toFixed(2)} • Used {sugg.count}x
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Qty and Price side by side on mobile */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Qty</label>
                        <input
                          type="number"
                          name="qty"
                          value={item.qty}
                          onChange={(e) => handleLineItemChange(index, e)}
                          className="w-full px-3 py-2.5 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 text-center"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Price ($)</label>
                        <input
                          type="number"
                          name="price"
                          value={item.price}
                          onChange={(e) => handleLineItemChange(index, e)}
                          className="w-full px-3 py-2.5 text-base border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 text-right"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                        <div className="px-3 py-2.5 text-base text-right font-bold text-purple-700 bg-purple-100 border-2 border-purple-300 rounded-lg">
                          ${item.amount.toFixed(2)}
                        </div>
                      </div>
                    </div>

                    {/* Remove button */}
                    {formData.lineItems.length > 1 && (
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition"
                        >
                          🗑️ Remove Item
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addLineItem}
                className="mt-4 w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 transition"
              >
                ➕ Add Another Item
              </button>
            </div>

            {/* Labor Section */}
            <div className="border-t pt-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Labor Performed</h3>
              <textarea
                name="laborPerformed"
                value={formData.laborPerformed}
                onChange={handleChange}
                rows="3"
                className={inputClass}
                placeholder="Describe the work performed..."
              />
            </div>

            {/* Totals */}
            <div className="border-t pt-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tax Rate</label>
                  <input
                    type="number"
                    name="taxRate"
                    value={formData.taxRate}
                    onChange={handleChange}
                    className={inputClass}
                    step="0.0001"
                    min="0"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount</label>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-3 max-w-md">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, discountMode: 'amount' }))}
                      className={`flex-1 py-2.5 text-sm font-semibold transition ${
                        formData.discountMode === 'amount'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Fixed ($)
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, discountMode: 'percent' }))}
                      className={`flex-1 py-2.5 text-sm font-semibold transition ${
                        formData.discountMode === 'percent'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      Percent (%)
                    </button>
                  </div>
                  {formData.discountMode === 'amount' ? (
                    <div>
                      <input
                        type="number"
                        name="discount"
                        value={formData.discount}
                        onChange={handleChange}
                        className={inputClass}
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                      <p className="mt-1 text-xs text-gray-500">Flat dollar amount subtracted after tax.</p>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="number"
                        name="discountPercent"
                        value={formData.discountPercent}
                        onChange={handleChange}
                        className={inputClass}
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="e.g. 10"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Percent of subtotal (before tax). Example: 10% on $100 subtotal = $10 off.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Subtotal:</span>
                  <span className="text-sm font-medium">${formData.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Tax:</span>
                  <span className="text-sm font-medium">${formData.taxAmount.toFixed(2)}</span>
                </div>
                {Number(formData.appliedDiscount || 0) > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600">{discountLineLabel}:</span>
                    <span className="text-sm font-medium text-red-600">
                      -${Number(formData.appliedDiscount || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-base font-bold text-gray-900">Total:</span>
                  <span className="text-base font-bold text-gray-900">${formData.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="border-t pt-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms & Conditions
              </label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                rows="2"
                className={inputClass}
              />
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 sm:justify-end pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="px-4 sm:px-6 py-3 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 transition-colors text-sm sm:text-base"
              >
                👁️ Preview
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-md hover:from-purple-700 hover:to-pink-700 transition-colors text-sm sm:text-base shadow-md"
              >
                📤 Share
              </button>
              <button
                type="button"
                onClick={downloadPDF}
                className="px-4 sm:px-6 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition-colors text-sm sm:text-base"
              >
                📥 Download
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 sm:px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 text-sm sm:text-base"
              >
                {isLoading ? 'Saving...' : '💾 Save'}
              </button>
            </div>
          </form>
        </div>

        {/* Invoice Preview */}
        <div className="bg-white shadow-lg rounded-lg p-4 md:p-8">
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Preview</h2>
          </div>
          
          <div ref={invoiceRef} className="bg-white p-8 border border-gray-300">
            {/* Invoice Header */}
            <div className="flex flex-col md:flex-row justify-between items-start mb-8 pb-6 border-b-2 border-purple-800">
              <div className="mb-4 md:mb-0">
                <img 
                  src="/images/qat.png" 
                  alt="Quality Air of Texas LLC" 
                  className="h-16 md:h-20 mb-2"
                />
                <h1 className="text-xl md:text-2xl font-bold text-purple-800">
                  Quality Air of Texas LLC
                </h1>
                <p className="text-sm text-gray-700">Address: 6023 Pennworth Ln, Houston, TX 77084</p>
                <p className="text-sm text-gray-700">Phone: (713) 922-5702</p>
                <p className="text-sm text-blue-600">https://qualityairoftexas.com</p>
                <p className="text-sm text-blue-600">Email: qualityairoftexas@gmail.com</p>
              </div>
              <div className="text-left md:text-right">
                <h2 className="text-3xl font-bold text-orange-500 mb-2">
                  {formData.isEstimate ? 'ESTIMATE' : 'INVOICE'}
                </h2>
                {formData.isEstimate && (
                  <p className="text-xs text-red-600 font-semibold">
                    ✓ Estimate (Valid for 30 DAYS)*
                  </p>
                )}
              </div>
            </div>

            {/* Service Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
              <div>
                <p><strong>Date:</strong> {formData.serviceDate}</p>
                <p><strong>Name:</strong> {formData.clientName || '_________________'}</p>
                <p><strong>Make:</strong> {formData.clientModel || '_________________'}</p>
              </div>
              <div>
                <p><strong>Invoice #:</strong> {formData.invoiceNumber}</p>
                <p><strong>Address:</strong> {formData.clientAddress || '_________________'}</p>
                <p><strong>Phone:</strong> {formData.clientPhone || '_________________'}</p>
              </div>
            </div>

            {/* Items Table */}
            <table className="w-full mb-6 text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 py-2 text-left">QTY</th>
                  <th className="border border-gray-300 px-2 py-2 text-left">PARTS - Description Of Parts & Materials</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">Price</th>
                  <th className="border border-gray-300 px-2 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {formData.lineItems.map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-2 py-2">{item.qty}</td>
                    <td className="border border-gray-300 px-2 py-2">{item.description}</td>
                    <td className="border border-gray-300 px-2 py-2 text-right">${parseFloat(item.price || 0).toFixed(2)}</td>
                    <td className="border border-gray-300 px-2 py-2 text-right">${parseFloat(item.amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Labor Section */}
            <div className="mb-6">
              <h3 className="font-bold mb-2 text-sm">Labor Performed:</h3>
              <div className="border border-gray-300 p-3 min-h-[80px] text-sm whitespace-pre-wrap">
                {formData.laborPerformed || 'We have one year workmanship warranty on labor and manufacture warranty on parts.'}
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-full md:w-1/2">
                <div className="flex justify-between border-b py-2">
                  <span className="font-semibold">Total Service:</span>
                  <span>${formData.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-b py-2">
                  <span className="font-semibold">TAX:</span>
                  <span>${formData.taxAmount.toFixed(2)}</span>
                </div>
                {Number(formData.appliedDiscount || 0) > 0 && (
                  <div className="flex justify-between border-b py-2">
                    <span className="font-semibold">{discountLineLabel}:</span>
                    <span className="text-red-600">-${Number(formData.appliedDiscount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-b-2 border-gray-800 py-2">
                  <span className="font-bold text-lg">Total Amount:</span>
                  <span className="font-bold text-lg">${formData.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t pt-4 text-xs text-gray-700">
              <p className="font-semibold mb-2">
                We Will Not Be Responsible For Loss or Damage Caused By: Fire, Theft, Testing, Or Any Other Causes beyond Our Control.
              </p>
              <p className="whitespace-pre-wrap mb-4">{formData.terms}</p>
              <p className="text-right italic text-gray-600 mt-4">
                Authorized By: Pejman Ansari
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full my-8 relative">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Create Custom Template</h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Quick Save Current Form */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">💡 Quick Save</h4>
              <p className="text-sm text-blue-700 mb-3">Save your current form data as a template</p>
              <button
                type="button"
                onClick={() => {
                  const name = window.prompt('Enter template name:');
                  if (name && name.trim()) {
                    saveCustomTemplate({
                      name: name.trim(),
                      items: formData.lineItems,
                      labor: formData.laborPerformed
                    });
                    setShowTemplateModal(false);
                  }
                }}
                className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
              >
                📋 Save Current Form as Template
              </button>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-semibold text-gray-800 mb-4">✨ Or Create New Template</h4>
              
              {/* Template Name */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Template Name *</label>
                <input
                  type="text"
                  value={newTemplateData.name}
                  onChange={(e) => setNewTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Emergency Weekend Service"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                />
              </div>

              {/* Line Items */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Items / Services</label>
                <div className="space-y-3">
                  {newTemplateData.items.map((item, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        {/* Description */}
                        <div className="md:col-span-6">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => {
                              const updated = [...newTemplateData.items];
                              updated[index].description = e.target.value;
                              setNewTemplateData(prev => ({ ...prev, items: updated }));
                            }}
                            placeholder="e.g., AC Unit Repair"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:border-purple-500"
                          />
                        </div>

                        {/* Qty */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Qty</label>
                          <input
                            type="number"
                            value={item.qty}
                            onChange={(e) => {
                              const updated = [...newTemplateData.items];
                              updated[index].qty = parseFloat(e.target.value) || 0;
                              updated[index].amount = updated[index].qty * updated[index].price;
                              setNewTemplateData(prev => ({ ...prev, items: updated }));
                            }}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:border-purple-500 text-center"
                          />
                        </div>

                        {/* Price */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Price ($)</label>
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => {
                              const updated = [...newTemplateData.items];
                              updated[index].price = parseFloat(e.target.value) || 0;
                              updated[index].amount = updated[index].qty * updated[index].price;
                              setNewTemplateData(prev => ({ ...prev, items: updated }));
                            }}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:border-purple-500 text-right"
                          />
                        </div>

                        {/* Amount & Remove */}
                        <div className="md:col-span-2 flex items-end gap-2">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                            <div className="px-3 py-2 bg-gray-100 rounded font-semibold text-right">
                              ${item.amount.toFixed(2)}
                            </div>
                          </div>
                          {newTemplateData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = newTemplateData.items.filter((_, i) => i !== index);
                                setNewTemplateData(prev => ({ ...prev, items: updated }));
                              }}
                              className="px-2 py-2 bg-red-500 hover:bg-red-600 text-white rounded"
                              title="Remove"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setNewTemplateData(prev => ({
                      ...prev,
                      items: [...prev.items, { qty: 1, description: '', price: 0, amount: 0 }]
                    }));
                  }}
                  className="mt-3 w-full px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium"
                >
                  ➕ Add Item
                </button>
              </div>

              {/* Labor */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Labor Performed (Optional)</label>
                <textarea
                  value={newTemplateData.labor}
                  onChange={(e) => setNewTemplateData(prev => ({ ...prev, labor: e.target.value }))}
                  placeholder="Describe the labor performed..."
                  rows="3"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 resize-y"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (!newTemplateData.name.trim()) {
                      alert('Please enter a template name');
                      return;
                    }
                    if (newTemplateData.items.length === 0 || !newTemplateData.items[0].description) {
                      alert('Please add at least one item');
                      return;
                    }
                    saveCustomTemplate(newTemplateData);
                    setNewTemplateData({
                      name: '',
                      items: [{ qty: 1, description: '', price: 0, amount: 0 }],
                      labor: ''
                    });
                    setShowTemplateModal(false);
                  }}
                  className="flex-1 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold"
                >
                  ✅ Save Template
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewTemplateData({
                      name: '',
                      items: [{ qty: 1, description: '', price: 0, amount: 0 }],
                      labor: ''
                    });
                    setShowTemplateModal(false);
                  }}
                  className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
            
            {/* Custom Templates List */}
            {Object.keys(customTemplates).length > 0 && (
              <div className="mt-6 border-t pt-6">
                <h4 className="font-semibold text-gray-700 mb-3">📚 Your Custom Templates ({Object.keys(customTemplates).length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {Object.entries(customTemplates).map(([key, template]) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                      <div>
                        <span className="font-medium text-gray-800">{template.name}</span>
                        <span className="text-sm text-gray-500 ml-2">({template.items.length} items)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete template "${template.name}"?`)) {
                            deleteCustomTemplate(key);
                          }
                        }}
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Preview Invoice</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Preview Content */}
            <div className="border-2 border-gray-300 rounded-lg p-6 bg-white">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <img src="/images/qat.png" alt="Logo" className="h-16 mb-2" />
                  <h2 className="text-2xl font-bold text-purple-800">Quality Air of Texas LLC</h2>
                  <p className="text-sm text-gray-600">6023 Pennworth Ln, Houston, TX 77084</p>
                  <p className="text-sm text-gray-600">Phone: (713) 922-5702</p>
                  <p className="text-sm text-blue-600">https://qualityairoftexas.com</p>
                </div>
                <div className="text-right">
                  <h3 className="text-3xl font-bold text-orange-500">
                    {formData.isEstimate ? 'ESTIMATE' : 'INVOICE'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">#{formData.invoiceNumber}</p>
                  <p className="text-sm text-gray-600">Date: {formData.serviceDate}</p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-2">Bill To:</h4>
                <p>{formData.clientName}</p>
                <p>{formData.clientAddress}</p>
                {formData.clientPhone && <p>Phone: {formData.clientPhone}</p>}
                {formData.clientEmail && <p>Email: {formData.clientEmail}</p>}
              </div>

              <table className="w-full mb-6">
                <thead className="bg-purple-100">
                  <tr>
                    <th className="text-left p-2">Qty</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-right p-2">Price</th>
                    <th className="text-right p-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.lineItems.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2">{item.qty}</td>
                      <td className="p-2">{item.description}</td>
                      <td className="text-right p-2">${parseFloat(item.price || 0).toFixed(2)}</td>
                      <td className="text-right p-2">${parseFloat(item.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex justify-end mb-6">
                <div className="w-64">
                  <div className="flex justify-between mb-2">
                    <span>Subtotal:</span>
                    <span>${formData.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Tax ({(formData.taxRate * 100).toFixed(2)}%):</span>
                    <span>${formData.taxAmount.toFixed(2)}</span>
                  </div>
                  {Number(formData.appliedDiscount || 0) > 0 && (
                    <div className="flex justify-between mb-2 text-red-600">
                      <span>{discountLineLabel}:</span>
                      <span>-${Number(formData.appliedDiscount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t-2 pt-2">
                    <span>Total:</span>
                    <span>${formData.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPreview(false);
                  handleSubmit(new Event('submit'));
                }}
                className="flex-1 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold"
              >
                ✅ Confirm & Save
              </button>
              <button
                onClick={() => setShowPreview(false)}
                className="flex-1 px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-semibold"
              >
                ✏️ Edit More
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* STICKY BOTTOM ACTION BAR (Mobile-First UX)   */}
      {/* ============================================ */}
      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t-2 border-purple-200 shadow-2xl backdrop-blur-md bg-opacity-95">
          {/* Total summary */}
          <div className="px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {formData.isEstimate ? 'Estimate' : 'Invoice'} #{formData.invoiceNumber.slice(-6)}
            </div>
            <div className="text-lg font-bold text-purple-700">
              ${formData.totalAmount.toFixed(2)}
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-4 gap-1 p-2 safe-area-inset-bottom">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex flex-col items-center justify-center py-2 rounded-xl bg-blue-50 text-blue-700 active:bg-blue-100 transition"
            >
              <span className="text-xl">👁️</span>
              <span className="text-xs font-medium mt-0.5">Preview</span>
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex flex-col items-center justify-center py-2 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white active:opacity-90 transition shadow-md"
            >
              <span className="text-xl">📤</span>
              <span className="text-xs font-medium mt-0.5">Share</span>
            </button>
            <button
              type="button"
              onClick={downloadPDF}
              className="flex flex-col items-center justify-center py-2 rounded-xl bg-green-50 text-green-700 active:bg-green-100 transition"
            >
              <span className="text-xl">📥</span>
              <span className="text-xs font-medium mt-0.5">PDF</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (navigator.vibrate) navigator.vibrate(15);
                try {
                  if (formRef.current) {
                    if (typeof formRef.current.requestSubmit === 'function') {
                      formRef.current.requestSubmit();
                    } else {
                      // Fallback for older browsers
                      handleSubmit({ preventDefault: () => {}, target: formRef.current });
                    }
                  } else {
                    handleSubmit({ preventDefault: () => {} });
                  }
                  // Scroll to top to see status message
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch (err) {
                  console.error('Sticky save error:', err);
                  setError('Save failed: ' + (err.message || 'Unknown error'));
                }
              }}
              disabled={isLoading}
              className="flex flex-col items-center justify-center py-2 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white active:opacity-90 disabled:opacity-50 transition shadow-md"
            >
              <span className="text-xl">{isLoading ? '⏳' : '💾'}</span>
              <span className="text-xs font-medium mt-0.5">{isLoading ? '...' : 'Save'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Add bottom padding when sticky bar is visible */}
      {showStickyBar && <div className="h-24 md:hidden" aria-hidden="true" />}

      {/* ============================================ */}
      {/* SHARE MODAL - Multi-channel sharing options  */}
      {/* ============================================ */}
      {showShareModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowShareModal(false);
          }}
        >
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl my-8 relative overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-5 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold flex items-center gap-2">
                      📤 Share {formData.isEstimate ? 'Estimate' : 'Invoice'}
                    </h3>
                    <p className="text-sm opacity-90 mt-1">
                      #{formData.invoiceNumber} • ${formData.totalAmount.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="text-white hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center text-2xl transition"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Status Message */}
              {shareStatus && (
                <div className={`px-5 py-3 border-b ${
                  shareStatus.includes('✅') ? 'bg-green-50 text-green-800' :
                  shareStatus.includes('❌') ? 'bg-red-50 text-red-800' :
                  shareStatus.includes('⚠️') ? 'bg-yellow-50 text-yellow-800' :
                  'bg-blue-50 text-blue-800'
                }`}>
                  <p className="text-sm font-medium">{shareStatus}</p>
                </div>
              )}

              {/* Body - Share Options */}
              <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">

                {/* SEND TO CLIENT EMAIL - Primary action */}
                <button
                  onClick={sendToClient}
                  disabled={shareLoading || !formData.clientEmail}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-between shadow-md"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-3xl">📧</span>
                    <div>
                      <div className="font-bold">Send to Client</div>
                      <div className="text-xs opacity-90">
                        {formData.clientEmail || '⚠️ No client email entered'}
                      </div>
                    </div>
                  </div>
                  <span className="text-xl">→</span>
                </button>

                {/* WHATSAPP */}
                <button
                  onClick={shareViaWhatsApp}
                  disabled={shareLoading}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition flex items-center justify-between shadow-md"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-3xl">💬</span>
                    <div>
                      <div className="font-bold">WhatsApp</div>
                      <div className="text-xs opacity-90">
                        {formData.clientPhone || 'Open WhatsApp Web'}
                      </div>
                    </div>
                  </div>
                  <span className="text-xl">→</span>
                </button>

                {/* SMS */}
                <button
                  onClick={shareViaSMS}
                  disabled={shareLoading}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-600 hover:to-indigo-700 disabled:opacity-50 transition flex items-center justify-between shadow-md"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-3xl">📱</span>
                    <div>
                      <div className="font-bold">Text Message (SMS)</div>
                      <div className="text-xs opacity-90">
                        {formData.clientPhone || 'Open SMS app'}
                      </div>
                    </div>
                  </div>
                  <span className="text-xl">→</span>
                </button>

                {/* EMAIL CLIENT (mailto) */}
                <button
                  onClick={shareViaEmail}
                  disabled={shareLoading}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition flex items-center justify-between shadow-md"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-3xl">✉️</span>
                    <div>
                      <div className="font-bold">Email App</div>
                      <div className="text-xs opacity-90">Open in your email client</div>
                    </div>
                  </div>
                  <span className="text-xl">→</span>
                </button>

                {/* COPY SUMMARY */}
                <button
                  onClick={copyInvoiceSummary}
                  disabled={shareLoading}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 transition flex items-center justify-between shadow-md"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-3xl">📋</span>
                    <div>
                      <div className="font-bold">Copy Summary</div>
                      <div className="text-xs opacity-90">Copy invoice text to clipboard</div>
                    </div>
                  </div>
                  <span className="text-xl">→</span>
                </button>

                {/* PRINT */}
                <button
                  onClick={printInvoice}
                  disabled={shareLoading}
                  className="w-full p-4 rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 text-white hover:from-slate-700 hover:to-slate-800 disabled:opacity-50 transition flex items-center justify-between shadow-md"
                >
                  <div className="flex items-center gap-3 text-left">
                    <span className="text-3xl">🖨️</span>
                    <div>
                      <div className="font-bold">Print</div>
                      <div className="text-xs opacity-90">Send to printer</div>
                    </div>
                  </div>
                  <span className="text-xl">→</span>
                </button>

                {/* NATIVE SHARE (Mobile) */}
                {typeof navigator !== 'undefined' && navigator.share && (
                  <button
                    onClick={shareNative}
                    disabled={shareLoading}
                    className="w-full p-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition flex items-center justify-between shadow-md"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <span className="text-3xl">📲</span>
                      <div>
                        <div className="font-bold">More Apps...</div>
                        <div className="text-xs opacity-90">Share via any app on your device</div>
                      </div>
                    </div>
                    <span className="text-xl">→</span>
                  </button>
                )}

                {shareLoading && (
                  <div className="flex items-center justify-center py-3">
                    <svg className="animate-spin h-6 w-6 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="ml-2 text-purple-700 font-medium">Working...</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-5 py-3 border-t">
                <p className="text-xs text-gray-500 text-center">
                  💡 Tip: Save the invoice first to keep a record in your admin panel
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PInvoicesPage;
